import React, {useEffect, useRef, useState} from 'react';
import {motion, useInView} from 'framer-motion';

import FeatureCard from './FeatureCard';

import {useMediaQuery} from '../../hooks/useMediaQuery';
import styles from './styles/main-05.module.css';

import {ReactComponent as ChartDownImg} from '../../assets/main05/chart-down.svg';
import {ReactComponent as ChartUpImg} from '../../assets/main05/chart-up.svg';
import {ReactComponent as DiagramLeftImg} from '../../assets/main05/diagram-left.svg';
import {ReactComponent as DiagramMainImg} from '../../assets/main05/diagram-main.svg';
import {ReactComponent as DiagramRightImg} from '../../assets/main05/diagram-right.svg';
import {ReactComponent as ErrorImg01} from '../../assets/main05/error01.svg';
import {ReactComponent as ErrorImg02} from '../../assets/main05/error02.svg';
import {ReactComponent as ErrorImg03} from '../../assets/main05/error03.svg';
import {ReactComponent as ErrorImg04} from '../../assets/main05/error04.svg';
import {ReactComponent as ErrorImg05} from '../../assets/main05/error05.svg';

const easeInMotion = {
  ease: 'easeIn',
  duration: 0.8
};

const FirstFeatureCard = ({ mobile }) => {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    amount: 0.2
  });

  const [step, setStep] = useState(0);

  useEffect(() => {
    if (isInView) {
      const timer1 = setTimeout(() => setStep(1), 600);    // 기본 요소
      const timer2 = setTimeout(() => setStep(2), 800);  // 사이드 이미지

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isInView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: isInView ? 1 : 0 }}
      transition={{ duration: 0.4 }}
    >
      <FeatureCard
        type='first'
        title={mobile ? 
          <p>회사의 모든 데이터, DARVIS는 다 알고 있어요</p> : 
          <p>회사의 모든 데이터,<br/>DARVIS는 다 알고 있어요</p>
        }
        subTitle={mobile ? 
          <p>ERP부터 문서까지, 궁금한 건 뭐든 질문하세요</p> : 
          <p>ERP부터 문서까지, 궁금한 건 뭐든<br/>질문하세요</p>
        }
        Image={DiagramMainImg}
        LeftImage={DiagramLeftImg}
        RightImage={DiagramRightImg}
        isVisible={step >= 1}
        showSideImages={step >= 2}
      />
    </motion.div>
  );
};

const SecondFeatureCard = ({ mobile }) => {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    amount: 0.3
  });

  const [step, setStep] = useState(0);

  useEffect(() => {
    if (isInView) {
      const timer1 = setTimeout(() => setStep(1), 200);    // 기본 요소
      const timer2 = setTimeout(() => setStep(2), 400);  // 차트 변환

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isInView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: isInView ? 1 : 0 }}
      transition={{ duration: 0.4 }}
    >
      <FeatureCard
        type='second'
        title={mobile ? 
          <p>매일 작성하던 보고서, 자동으로 완성돼요</p> : 
          <p>매일 작성하던 보고서,<br/>자동으로 완성돼요</p>
        }
        subTitle={mobile ? 
          <p>필요한 데이터, 원하는 형태로 받아보세요</p> : 
          <p>필요한 데이터, 원하는 형태로<br/>받아보세요</p>
        }
        Image={ChartUpImg}
        LastImage={ChartDownImg}
        isVisible={step >= 1}
        showLastImage={step >= 2}
      />
    </motion.div>
  );
};

const ThirdFeatureCard = ({ mobile }) => {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    amount: 0.3
  });

  const [step, setStep] = useState(0);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setStep(1), 0);  // 모든 요소 표시

      return () => clearTimeout(timer);
    }
  }, [isInView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: isInView ? 1 : 0 }}
      transition={{ duration: 0.4 }}
    >
      <FeatureCard
        type='third'
        title={mobile ? 
          <p>놓치기 쉬운 문제, 먼저 찾아드려요</p> : 
          <p>놓치기 쉬운 문제,<br/>먼저 찾아드려요</p>
        }
        subTitle={mobile ? 
          <p>작은 오류부터 큰 문제까지, DARVIS가 실시간으로 분석해서 알려줘요</p> : 
          <p>작은 오류부터 큰 문제까지,<br/>DARVIS가 실시간으로 분석해서<br/>알려줘요</p>
        }
        Image={ErrorImg01}
        SecondImage={ErrorImg02}
        ThirdImage={ErrorImg03}
        FourImage={ErrorImg04}
        FiveImage={ErrorImg05}
        isVisible={step >= 1}
      />
    </motion.div>
  );
};

export default function Main05() {
  const mobile = useMediaQuery('(max-width: 1024px)');
  const mobileSmall = useMediaQuery('(max-width: 480px)');
  const mobileMedium = useMediaQuery('(min-width: 481px) and (max-width: 768px)');

  const ref = useRef(null);
      
  const isInView = useInView(ref, {
    once: true,
    amount: mobile ? 0.1 : 0.2,
    margin: mobileMedium ? '-20px 0px' : '-100px 0px'
  });

  return (
    <div className={styles.wrapper}>
       <div className={styles.container} ref={ref}>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={easeInMotion}
        >
          {mobileSmall 
            ? <>단절된 데이터,<br />DARVIS가 연결해드려요!</>
            : '단절된 데이터, DARVIS가 연결해드려요!'
          }
        </motion.p>
        <motion.div
          className={styles.boxCard}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
        >
          <FirstFeatureCard mobile={mobile} />
          <SecondFeatureCard mobile={mobile} />
          <ThirdFeatureCard mobile={mobile} />
        </motion.div>
      </div>
    </div>
  );
}
